import { lazy, Suspense, type CSSProperties } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthLoadingScreen } from '@/components/auth/auth-loading-screen';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ConsoleLayout } from '@/layouts/ConsoleLayout';
import { useThemeStore } from '@/stores/themeStore';

const SignIn = lazy(() => import('./pages/auth/SignIn'));
const SignUp = lazy(() => import('./pages/auth/SignUp'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));

const OverviewPage = lazy(() => import('./pages/overview/OverviewPage'));
const MapPage = lazy(() => import('./pages/map/MapPage'));
const DevicesPage = lazy(() => import('./pages/devices/DevicesPage'));
const DeviceDetailPage = lazy(() => import('./pages/devices/DeviceDetailPage'));
const HistoryPage = lazy(() => import('./pages/history/HistoryPage'));
const PlacesPage = lazy(() => import('./pages/places/PlacesPage'));
const AlertsPage = lazy(() => import('./pages/alerts/AlertsPage'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const WelcomePage = lazy(() => import('./pages/welcome/WelcomePage'));
const NotFound = lazy(() => import('./pages/NotFound'));


const DeviceSettingsPage = lazy(() => import('./pages/devices/DeviceSettingsPage'));
const PlaceDetailPage = lazy(() => import('./pages/places/PlaceDetailPage'));
const RulesPage = lazy(() => import('./pages/rules/RulesPage'));
const RuleDetailPage = lazy(() => import('./pages/rules/RuleDetailPage'));
const AlertDetailPage = lazy(() => import('./pages/alerts/AlertDetailPage'));
const OrderDetailPage = lazy(() => import('./pages/billing/OrderDetailPage'));
const ShipmentsPage = lazy(() => import('./pages/shipments/ShipmentsPage'));
const ShipmentDetailPage = lazy(() => import('./pages/shipments/ShipmentDetailPage'));
const ShareLinksPage = lazy(() => import('./pages/sharing/ShareLinksPage'));
const ShareLinkDetailPage = lazy(() => import('./pages/sharing/ShareLinkDetailPage'));
const ShipmentNewPage = lazy(() => import('./pages/shipments/ShipmentNewPage'));
const ShareLinkNewPage = lazy(() => import('./pages/sharing/ShareLinkNewPage'));
const RecipientSharePage = lazy(() => import('./pages/sharing/RecipientSharePage'));
const TeamPage = lazy(() => import('./pages/workspace/TeamPages').then((m) => ({ default: m.TeamPage })));
const InvitePage = lazy(() => import('./pages/workspace/TeamPages').then((m) => ({ default: m.InvitePage })));
const PlaceEditorPage = lazy(() => import('./pages/places/PlaceEditorPage'));
const RuleEditorPage = lazy(() => import('./pages/rules/RuleEditorPage'));

function LegacyAssetRoute() {
  const { deviceId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`${deviceId ? `/assets/${deviceId}` : '/assets'}${search}`} replace />;
}
function LegacyHistoryRoute() {
  const [params] = useSearchParams();
  const id = params.get('device');
  const day = params.get('day');
  return <Navigate to={id ? `/assets/${encodeURIComponent(id)}/history${day ? `?day=${encodeURIComponent(day)}` : ''}` : '/assets'} replace />;
}

const TOASTER_STYLE = {
  '--normal-bg': 'var(--surface)',
  '--normal-border': 'var(--line)',
  '--normal-text': 'var(--foreground)',
  fontFamily: 'var(--font-sans)',
} as CSSProperties;

export default function App() {
  const theme = useThemeStore((s) => s.resolved);

  return (
    <BrowserRouter>
      <TooltipProvider delayDuration={200}>
        <Suspense fallback={<AuthLoadingScreen />}>
          <Routes>
            <Route path="/s/:token" element={<RecipientSharePage />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route
              element={
                <ProtectedRoute>
                  <ConsoleLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/map" replace />} />
              <Route path="map" element={<MapPage />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="assets" element={<DevicesPage />} />
              <Route path="assets/new" element={<WelcomePage />} />
              <Route path="assets/:assetId" element={<DeviceDetailPage />} />
              <Route path="assets/:assetId/history" element={<HistoryPage />} />
              <Route path="assets/:assetId/settings" element={<DeviceSettingsPage />} />
              <Route path="shipments" element={<ShipmentsPage />} />
              <Route path="shipments/new" element={<ShipmentNewPage />} />
              <Route path="shipments/:shipmentId" element={<ShipmentDetailPage />} />
              <Route path="places" element={<PlacesPage />} />
              <Route path="places/new" element={<PlaceEditorPage />} />
              <Route path="places/:placeId" element={<PlaceDetailPage />} />
              <Route path="places/:placeId/edit" element={<PlaceEditorPage />} />
              <Route path="rules" element={<RulesPage />} />
              <Route path="rules/new" element={<RuleEditorPage />} />
              <Route path="rules/:ruleId" element={<RuleDetailPage />} />
              <Route path="rules/:ruleId/edit" element={<RuleEditorPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="alerts/:alertId" element={<AlertDetailPage />} />
              <Route path="share-links" element={<ShareLinksPage />} />
              <Route path="share-links/new" element={<ShareLinkNewPage />} />
              <Route path="share-links/:shareLinkId" element={<ShareLinkDetailPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="team/invite" element={<InvitePage />} />
              <Route path="orders" element={<BillingPage />} />
              <Route path="orders/:orderId" element={<OrderDetailPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/:section" element={<SettingsPage />} />
              <Route path="devices" element={<LegacyAssetRoute />} />
              <Route path="devices/:deviceId" element={<LegacyAssetRoute />} />
              <Route path="history" element={<LegacyHistoryRoute />} />
              <Route path="billing" element={<Navigate to="/orders" replace />} />
              <Route path="welcome" element={<Navigate to="/assets/new" replace />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
        <Toaster theme={theme} position="bottom-right" style={TOASTER_STYLE} offset={16} />
      </TooltipProvider>
    </BrowserRouter>
  );
}
